import React, { useState, useEffect, useRef } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const CategoryDetails = ({
  category,
  categories,
  onCategoryUpdate,
  onCategoryCreate,
  handleSelectCancel,
  isEditing,
  setIsEditing,
}) => {
  const { t } = useTranslation();

  const { categoryInfo } = useSelector((state) => state.categories);
  const [formData, setFormData] = useState({
    translations: {
      en: { name: "" },
      ar: { name: "" },
    },
    image: null,
    imagePreview: "",
    order: 0,
  });

  const fileInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (category) {
      setFormData({
        translations: {
          en: { name: category.translations?.en?.name || "" },
          ar: { name: category.translations?.ar?.name || "" },
        },
        image: category.image || null,
        imagePreview: category.image || "",
        order: category.order || 0,
      });
    } else {
      setFormData({
        translations: {
          en: { name: "" },
          ar: { name: "" },
        },
        image: null,
        imagePreview: "",
        order: 0,
      });
    }
    setErrors({});
  }, [category]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleTranslationChange = (lang, field, value) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: { ...prev.translations[lang], [field]: value },
      },
    }));
    if (errors[`${lang}.${field}`]) {
      setErrors((prev) => ({ ...prev, [`${lang}.${field}`]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          image: t("imageError"),
        }));
        return;
      }

      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          image: "File size must be less than 5MB",
        }));
        return;
      }

      // Clear any previous errors
      if (errors.image) {
        setErrors((prev) => ({ ...prev, image: "" }));
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          image: file,
          imagePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
      imagePreview: "",
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.translations.ar.name.trim()) {
      newErrors["ar.name"] = t("arNameError");
    }

    if (!formData.image && !formData.imagePreview) {
      newErrors.image = t("imageRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const categoryData = {
      ...formData,
      id: category?.id || Date.now().toString(),
    };

    if (category) {
      onCategoryUpdate(categoryData);
    } else {
      onCategoryCreate(categoryData);
    }

    setIsEditing(false);
  };

  const handleCancel = () => {
    if (category) {
      setFormData({
        translations: {
          en: { name: category.translations?.en?.name || "" },
          ar: { name: category.translations?.ar?.name || "" },
        },
        image: category.image || null,
        imagePreview: category.image || "",
        order: category.order || 0,
      });
    }
    setErrors({});
    setIsEditing(false);
  };

  if (
    (!category && !isEditing && categoryInfo == null) ||
    (categoryInfo == null && !isEditing)
  ) {
    return (
      <div className="h-full flex items-center justify-center text-center">
        <div>
          <Icon
            name="FolderOpen"
            size={64}
            className="mx-auto mb-4 text-muted-foreground opacity-50"
          />
          <h3 className="text-lg font-medium text-foreground mb-2">
            {t("noCategorySelected")}
          </h3>
          <p className="text-muted-foreground mb-6">{t("selectCategory")}</p>
          <Button
            variant="outline"
            onClick={() => {
              setIsEditing(true);
              setFormData({
                translations: {
                  en: { name: "" },
                  ar: { name: "" },
                },
                image: null,
                imagePreview: "",
                order: 0,
              });
            }}
            iconName="Plus"
            iconPosition="left"
          >
            {t("createNewCategory")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-md bg-surface flex items-center justify-center">
            <Icon name={formData.icon} size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {category ? t("categoryDetails") : t("createCategory")}
            </h2>
            {category && (
              <p className="text-sm text-muted-foreground">
                ID: {category.id} • تم الإنشاء
                {new Date(category.created_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancel}>
                {t("cancel")}
              </Button>
              <Button onClick={handleSave} iconName="Save" iconPosition="left">
                {t("saveChanges")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleSelectCancel}>
                {t("cancel")}
              </Button>
              <Button
                onClick={() => setIsEditing(true)}
                iconName="Edit"
                iconPosition="left"
              >
                {t("editCategory")}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">
              {t("basicInformation")}
            </h3>

            {/*  <Input
              label={t('englishCategoryName')}
              type="text"
              value={formData.translations.en.name}
              onChange={(e) =>
                handleTranslationChange("en", "name", e.target.value)
              }
              placeholder={t('englishCategoryName')}
              disabled={!isEditing}
              error={errors["en.name"]}
              required
            />*/}

            <Input
              label={t("arabicCategoryName")}
              type="text"
              value={formData.translations.ar.name}
              onChange={(e) =>
                handleTranslationChange("ar", "name", e.target.value)
              }
              placeholder={t("arabicCategoryName")}
              disabled={!isEditing}
              error={errors["ar.name"]}
              required
            />

            {/* Image Upload Section */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {t("categoryImage")}{" "}
                {isEditing && <span className="text-error">*</span>}
              </label>

              {formData.imagePreview ? (
                <div className="relative w-full max-w-xs">
                  <img
                    src={formData.imagePreview}
                    alt=""
                    className="rounded-md border border-border w-full h-auto"
                  />
                  {isEditing && (
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 bg-error text-white rounded-full p-1 hover:bg-error/90 transition-micro"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  )}
                </div>
              ) : (
                <div className="border-2 border-dashed border-border rounded-md p-4 text-center">
                  <p className="text-muted-foreground mb-2">
                    لم يتم اختيار صورة{" "}
                  </p>
                </div>
              )}

              {isEditing && (
                <>
                  <img
                    src={category?.image}
                    alt=""
                    className="rounded-md border border-border w-full h-auto"
                  />
                  <div className="space-y-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept="image/*"
                      className="hidden"
                      id="category-image-upload"
                    />
                    <label
                      htmlFor="category-image-upload"
                      className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background border border-input hover:bg-accent hover:text-accent-foreground h-10 py-2 px-4 cursor-pointer"
                    >
                      <Icon name="Upload" className="mr-2" size={16} />
                      {formData.imagePreview
                        ? t("changeImage")
                        : t("uploadImage")}
                    </label>
                    <p className="text-xs text-muted-foreground">
                      {t("imageFormats")}
                    </p>
                  </div>
                </>
              )}

              {errors.image && (
                <p className="text-sm text-error">{errors.image}</p>
              )}
            </div>

            <Input
              label={t("order")}
              type="number"
              value={formData.order}
              onChange={(e) => handleInputChange("order", e.target.value)}
              placeholder={t("order")}
              disabled={!isEditing}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryDetails;
